import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { RiDeleteBinLine } from "@remixicon/react";

import Button from "components/Button";
import Modal, { ModalButtonsSection, ModalContent, ModalSections } from "v2/components/billing/modals";
import Spinner from "components/Spinner";

import { useApiKeys } from "v2/api-key-management/hooks/useApiKeys";
import { useRedirectFromActionData } from "hooks/useRedirectFromActionData";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";

import { ApiKeysInputName, path as ApiKeysDataRoutePath } from "v2/api-key-management/routes/ApiKeysDataRoute";
import { path as ApiKeyManagementPath } from "v2/api-key-management/routes/ApiKeyManagementRoute";
import { path as NewApiKeyConfirmationPath } from "v2/api-key-management/routes/NewApiKeyConfirmationRoute";
import { path as DeleteApiKeyConfirmationPath } from "v2/api-key-management/routes/DeleteApiKeyConfirmationRoute";

import { ApiKey } from "types/api-keys/ApiKey";

import { cx, fmtDate } from "utils";


function ApiKeyListElement({ apiKey, apiKeyToRemove, disableDeleteButtons, isLoading }: { apiKey: ApiKey, apiKeyToRemove: string | undefined, disableDeleteButtons: boolean, isLoading: boolean }) {
  const location = useLocation();
  const isAboutToDelete = apiKey.apiKey === apiKeyToRemove;

  return (
    <div
      className={ cx("flex flex-row justify-between", isLoading && "blur-sm animate-pulse") }
    >
      <div className={ cx("flex flex-col", isAboutToDelete && "text-red dark:text-error-600") }>
        <div className={ cx("font-mono", !apiKey.expireAt && "font-bold") }>{ apiKey.apiKey }</div>

        { apiKey.expireAt ? (
          <div className="text-sm">Expires at { fmtDate(new Date(apiKey.expireAt)) }</div>
        ) : (apiKey.addedAt &&
            <div className="text-sm">Active since { fmtDate(new Date(apiKey.addedAt)) }</div>
        ) }
      </div>

      { apiKey.expireAt && (
        <div className="flex items-center">
          <Button
            href={ DeleteApiKeyConfirmationPath }
            className="button button-tertiary"
            size="SM"
            disabled={ disableDeleteButtons }
            icon={ {
              Icon: RiDeleteBinLine,
              size: "size-5",
              style: cx("transition-colors text-lightGray dark:text-neutral-200", !disableDeleteButtons && "hover:text-darkGray hover:dark:text-neutral-800")
            } }
            // TODO must pass backgroundLocation here to be backward compatible when these dialogs are used in pages which don't use data routes yet
            navigationState={ { apiKey: apiKey.apiKey, backgroundLocation: location.state.backgroundLocation } }
            useViewTransitions
          />
        </div>
      ) }
    </div>
  );
}

export default function ApiKeyManagementModal() {
  const { apiKeys, apiKeysFetcher, reloadApiKeys } = useApiKeys(false);
  const location = useLocation();
  const confirmNewKeyPath = "/" + ApiKeyManagementPath + "/" + NewApiKeyConfirmationPath;
  const confirmDeleteKeyPath = "/" + ApiKeyManagementPath + "/" + DeleteApiKeyConfirmationPath;
  const isNewConfirmation = location.pathname.endsWith(confirmNewKeyPath);
  const isDeleteConfirmation = location.pathname.endsWith(confirmDeleteKeyPath);
  const backgroundLocation = location.pathname.slice(0, isNewConfirmation ? -confirmNewKeyPath.length : (isDeleteConfirmation ? -confirmDeleteKeyPath.length : -(ApiKeyManagementPath.length + 1)));
  const actionRedirect = location.pathname.slice(0, location.pathname.indexOf("/" + ApiKeyManagementPath) + (ApiKeyManagementPath.length + 1));

  const [ opened, setOpened ] = useState(false);

  useEffect(() => {
    if (!opened) {
      setOpened(true);
      reloadApiKeys();
    }
  }, [ apiKeys, opened, setOpened, reloadApiKeys ]);

  // display toaster and redirect if the action tells to
  useRedirectFromActionData(apiKeysFetcher);
  useToasterFromActionData(apiKeysFetcher);

  return (
    <Modal headline="Manage API Keys" goToOnClose={ backgroundLocation }>
      <apiKeysFetcher.Form method="POST" action={ ApiKeysDataRoutePath }>
        <input type="hidden" name={ ApiKeysInputName.redirect } value={ actionRedirect } />

        <ModalContent>
          <ModalSections>
            { (!apiKeys && (apiKeysFetcher?.state === "loading")) && (
              <Spinner size="XS" />
            ) }

            { apiKeys?.map(apiKey =>
              <ApiKeyListElement
                key={ apiKey.apiKey }
                apiKey={ apiKey }
                apiKeyToRemove={ location.state?.apiKey }
                disableDeleteButtons={ isNewConfirmation || isDeleteConfirmation }
                isLoading={ (apiKeysFetcher.state === "loading") && (apiKeysFetcher.formData === undefined) }
              />
            ) }
          </ModalSections>
          { (isNewConfirmation || isDeleteConfirmation) ? <Outlet /> : (
            <ModalButtonsSection>
              <Button
                text="New API Key"
                size="SM"
                href={ NewApiKeyConfirmationPath }
                useViewTransitions
                // TODO must pass backgroundLocation here to be backward compatible when these dialogs are used in pages which don't use data routes yet
                navigationState={ { backgroundLocation: location.state?.backgroundLocation } }
                disabled={ apiKeysFetcher?.state !== "idle" }
              />
            </ModalButtonsSection>
          ) }
        </ModalContent>
      </apiKeysFetcher.Form>
    </Modal>
  );
};
